import { useQuery, useQueryClient } from '@tanstack/react-query';

import { DocumentStatusGroupingResource } from '../../../../types';
import { useSubscribeToDocumentStatusUpdated } from '../../../../websocketApi';
import { useRequestConstructor } from '../../../hooks/useRequestConstructor';
import { unwrapNonPaginatedResponseDataAs } from '../../../logic/unwrapNonPaginatedResponseDataAs';
import { NonPaginatedResponse } from '../../../types/NonPaginatedResponse';
import { DOCUMENTS_BASE_PATH } from '../../constants/documentsBasePath';
import { updateQueryDataAtKey } from '../../logic/updateQueryDataAtKey';
import { getDocumentStatusGrouping } from '../../requests/getDocumentStatusGrouping';
import { DocumentParams } from '../../types/DocumentParams';

export const useDocumentStatusGrouping = (
  documentParams: DocumentParams,
  documentId: string,
  groupingKey: string,
) => {
  const { constructGet } = useRequestConstructor();
  const queryClient = useQueryClient();

  const queryKey = [
    DOCUMENTS_BASE_PATH,
    'status',
    documentId,
    documentParams.definition,
    'grouping',
    groupingKey,
  ];

  const { data, refetch, ...rest } = useQuery<
    Awaited<ReturnType<typeof getDocumentStatusGrouping>>
  >({
    queryKey,
    queryFn: async () =>
      getDocumentStatusGrouping(
        constructGet,
        documentParams,
        documentId,
        groupingKey,
      ),
    enabled: Boolean(documentId && documentParams.definition),
    keepPreviousData: true,
    staleTime: 5 * 60 * 1000,
  });

  useSubscribeToDocumentStatusUpdated(documentId, ({ status }) => {
    updateQueryDataAtKey<NonPaginatedResponse<DocumentStatusGroupingResource>>(
      queryClient,
      queryKey,
      { data: status },
    );
  });

  return {
    ...unwrapNonPaginatedResponseDataAs(
      data,
      refetch,
      'documentStatusGrouping',
    ),
    ...rest,
  };
};
